'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import StatusBadge from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import { InventoryItem } from './inventoryData';
import {
  Package, Eye, Edit2, ArrowUpDown, ArrowUp, ArrowDown,
  AlertTriangle, AlertCircle, CheckCircle2, MinusCircle,
  MoreHorizontal, TrendingDown, RefreshCw,
} from 'lucide-react';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

type SortKey = keyof InventoryItem;

interface Props {
  items: InventoryItem[];
  allItems: InventoryItem[];
  onItemUpdate: (item: InventoryItem) => void;
}

function StockStatusIcon({ status }: { status: string }) {
  if (status === 'in-stock') return <CheckCircle2 size={13} style={{ color: 'var(--success)' }} />;
  if (status === 'low') return <AlertTriangle size={13} style={{ color: 'var(--warning)' }} />;
  if (status === 'critical') return <AlertCircle size={13} style={{ color: 'var(--danger)' }} />;
  return <MinusCircle size={13} style={{ color: 'var(--muted-foreground)' }} />;
}

function StockLevelBar({ current, min, reorder, max }: { current: number; min: number; reorder: number; max: number }) {
  const pct = Math.min(100, Math.round((current / max) * 100));
  const color = current === 0 ? 'var(--muted-foreground)' : current < min ? 'var(--danger)' : current < reorder ? 'var(--warning)' : 'var(--success)';
  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-0.5">
        <span className="text-xs font-tabular font-semibold text-foreground">{current.toLocaleString()}</span>
        <span className="text-xs text-muted-foreground font-tabular">{pct}%</span>
      </div>
      <div className="h-1.5 rounded-full w-full" style={{ backgroundColor: 'var(--muted)' }}>
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

const COLUMNS = [
  { key: 'itemCode' as SortKey, label: 'Code', width: '80px' },
  { key: 'itemName' as SortKey, label: 'Item Name', width: '220px' },
  { key: 'category' as SortKey, label: 'Category', width: '110px' },
  { key: 'store' as SortKey, label: 'Store', width: '140px' },
  { key: 'currentQty' as SortKey, label: 'Stock Level', width: '150px' },
  { key: 'unit' as SortKey, label: 'Unit', width: '70px' },
  { key: 'reorderLevel' as SortKey, label: 'Reorder At', width: '90px', align: 'right' as const },
  { key: 'unitCost' as SortKey, label: 'Unit Cost', width: '100px', align: 'right' as const },
  { key: 'totalValue' as SortKey, label: 'Total Value', width: '120px', align: 'right' as const },
  { key: 'expiryDate' as SortKey, label: 'Expiry', width: '100px' },
  { key: 'stockStatus' as SortKey, label: 'Status', width: '110px', align: 'center' as const },
];

function SortIcon({ col, sortCol, sortDir }: { col: SortKey; sortCol: SortKey; sortDir: 'asc' | 'desc' }) {
  if (col !== sortCol) return <ArrowUpDown size={11} className="text-muted-foreground opacity-40" />;
  return sortDir === 'asc' ? <ArrowUp size={11} style={{ color: 'var(--primary)' }} /> : <ArrowDown size={11} style={{ color: 'var(--primary)' }} />;
}

function ExpiryCell({ date }: { date: string | null }) {
  if (!date) return <span className="text-xs text-muted-foreground">—</span>;
  const today = new Date('2026-09-04');
  const exp = new Date(date.split('/').reverse().join('-'));
  const daysLeft = Math.round((exp.getTime() - today.getTime()) / 86400000);
  const color = daysLeft < 0 ? 'var(--danger)' : daysLeft < 30 ? 'var(--warning)' : 'var(--muted-foreground)';
  return (
    <div>
      <p className="text-xs font-tabular" style={{ color }}>{date}</p>
      {daysLeft < 60 && (
        <p className="text-xs mt-0.5" style={{ color, fontWeight: daysLeft < 30 ? 600 : 400 }}>
          {daysLeft < 0 ? 'Expired' : `${daysLeft}d left`}
        </p>
      )}
    </div>
  );
}

export default function InventoryTable({ items, onItemUpdate }: Props) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortCol, setSortCol] = useState<SortKey>('category');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);

  function handleSort(col: SortKey) {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  }

  const sorted = [...items].sort((a, b) => {
    const av = a[sortCol] as string | number | null;
    const bv = b[sortCol] as string | number | null;
    if (av === null) return 1;
    if (bv === null) return -1;
    const cmp = typeof av === 'number' && typeof bv === 'number' ? av - bv : String(av).localeCompare(String(bv));
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalPages = Math.ceil(sorted.length / pageSize);
  const paginated = sorted.slice((page - 1) * pageSize, page * pageSize);

  if (items.length === 0) {
    return (
      <div className="panel-card">
        <EmptyState
          icon={Package}
          title="No inventory items found"
          description="No items match your current filters. Adjust the search or category filters, or add a new inventory item."
          action={{ label: 'Add Inventory Item', onClick: () => toast.info('Add item form — coming soon') }}
        />
      </div>
    );
  }

  return (
    <div className="panel-card">
      <div className="overflow-x-auto scrollbar-thin">
        <table className="w-full" style={{ minWidth: '1300px' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
              {COLUMNS.map((col) => (
                <th
                  key={`inv-th-${col.key}`}
                  className="px-3 py-3"
                  style={{ width: col.width, textAlign: col.align ?? 'left' }}
                >
                  <button
                    onClick={() => handleSort(col.key)}
                    className="flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                    style={{
                      letterSpacing: '0.02em',
                      justifyContent: col.align === 'right' ? 'flex-end' : col.align === 'center' ? 'center' : 'flex-start',
                    }}
                  >
                    {col.label}
                    <SortIcon col={col.key} sortCol={sortCol} sortDir={sortDir} />
                  </button>
                </th>
              ))}
              <th className="px-3 py-3 w-20">
                <span className="text-xs font-semibold text-muted-foreground" style={{ letterSpacing: '0.02em' }}>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.map((item) => {
              const rowBg = item.stockStatus === 'out-of-stock' ?'#fff8f8'
                : item.stockStatus === 'critical' ?'#fff9f9'
                : undefined;

              return (
                <tr
                  key={item.id}
                  className="table-row-hover border-b transition-colors"
                  style={{ borderColor: 'var(--border)', backgroundColor: rowBg }}
                >
                  {/* Code */}
                  <td className="px-3 py-3">
                    <span className="text-xs font-mono font-semibold" style={{ color: 'var(--primary)' }}>{item.itemCode}</span>
                  </td>

                  {/* Name */}
                  <td className="px-3 py-3">
                    <div className="flex items-start gap-2">
                      <StockStatusIcon status={item.stockStatus} />
                      <div>
                        <p className="text-sm font-medium text-foreground leading-tight" style={{ maxWidth: '200px' }}>{item.itemName}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">{item.supplier}</p>
                      </div>
                    </div>
                  </td>

                  {/* Category */}
                  <td className="px-3 py-3">
                    <span className="text-xs font-medium text-foreground">{item.category}</span>
                  </td>

                  {/* Store */}
                  <td className="px-3 py-3">
                    <p className="text-xs text-foreground">{item.store}</p>
                  </td>

                  {/* Stock Level */}
                  <td className="px-3 py-3" style={{ minWidth: '130px' }}>
                    <StockLevelBar
                      current={item.currentQty}
                      min={item.minStock}
                      reorder={item.reorderLevel}
                      max={item.maxStock}
                    />
                    {item.daysRemaining !== null && (
                      <p className="text-xs mt-1 font-medium font-tabular" style={{ color: item.daysRemaining < 4 ? 'var(--danger)' : 'var(--warning)' }}>
                        ~{item.daysRemaining}d remaining
                      </p>
                    )}
                  </td>

                  {/* Unit */}
                  <td className="px-3 py-3">
                    <span className="text-xs text-muted-foreground">{item.unit}</span>
                  </td>

                  {/* Reorder Level */}
                  <td className="px-3 py-3 text-right">
                    <span className="text-xs font-tabular text-muted-foreground">{item.reorderLevel.toLocaleString()}</span>
                  </td>

                  {/* Unit Cost */}
                  <td className="px-3 py-3 text-right">
                    <span className="text-xs font-tabular text-foreground">UGX {item.unitCost.toLocaleString()}</span>
                  </td>

                  {/* Total Value */}
                  <td className="px-3 py-3 text-right">
                    <span className="text-sm font-semibold font-tabular text-foreground">
                      {item.totalValue === 0
                        ? <span className="text-muted-foreground">—</span>
                        : `UGX ${(item.totalValue / 1000).toFixed(0)}K`
                      }
                    </span>
                  </td>

                  {/* Expiry */}
                  <td className="px-3 py-3">
                    <ExpiryCell date={item.expiryDate} />
                  </td>

                  {/* Status */}
                  <td className="px-3 py-3 text-center">
                    <StatusBadge status={item.stockStatus} size="sm" />
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1 justify-end relative">
                      <button
                        className="btn-ghost p-1.5 group relative"
                        aria-label="View item details"
                        onClick={() => toast.info(`Item details: ${item.itemName}`)}
                      >
                        <Eye size={13} />
                        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-1.5 py-0.5 text-xs bg-foreground text-background rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                          View details
                        </span>
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setActionMenuId(actionMenuId === item.id ? null : item.id)}
                          className="btn-ghost p-1.5"
                          aria-label="More actions"
                        >
                          <MoreHorizontal size={13} />
                        </button>
                        {actionMenuId === item.id && (
                          <div
                            className="absolute right-0 top-full mt-1 w-40 bg-card rounded-lg border shadow-modal py-1 z-20 fade-in"
                            style={{ borderColor: 'var(--border)' }}
                          >
                            <button onClick={() => { toast.info('Issue stock — GIN form coming soon'); setActionMenuId(null); }} className="flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted w-full transition-colors">
                              <TrendingDown size={12} /> Issue Stock (GIN)
                            </button>
                            <button onClick={() => { toast.info('Receive stock — GRN form coming soon'); setActionMenuId(null); }} className="flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted w-full transition-colors">
                              <RefreshCw size={12} /> Receive Stock (GRN)
                            </button>
                            <button onClick={() => { toast.info('Edit item — form coming soon'); setActionMenuId(null); }} className="flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-muted w-full transition-colors">
                              <Edit2 size={12} /> Edit Item
                            </button>
                            <div className="border-t my-1" style={{ borderColor: 'var(--border)' }} />
                            <button onClick={() => { toast.info('Stock adjustment form — coming soon'); setActionMenuId(null); }} className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted w-full transition-colors" style={{ color: 'var(--warning)' }}>
                              <AlertTriangle size={12} /> Adjust Stock
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="select-field py-1 px-2 text-xs"
            style={{ height: '28px', width: '60px' }}
          >
            {PAGE_SIZE_OPTIONS.map((s) => <option key={`inv-ps-${s}`} value={s}>{s}</option>)}
          </select>
          <span>
            Showing {Math.min((page - 1) * pageSize + 1, sorted.length)}–{Math.min(page * pageSize, sorted.length)} of {sorted.length} items
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setPage(1)} disabled={page === 1} className="btn-ghost px-2 py-1 text-xs disabled:opacity-40">«</button>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-ghost px-2 py-1 text-xs disabled:opacity-40">‹</button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const start = Math.max(1, Math.min(page - 2, totalPages - 4));
            const pageNum = start + i;
            if (pageNum > totalPages) return null;
            return (
              <button
                key={`inv-page-${pageNum}`}
                onClick={() => setPage(pageNum)}
                className="px-2.5 py-1 text-xs rounded font-medium transition-colors"
                style={{ backgroundColor: page === pageNum ? 'var(--primary)' : 'transparent', color: page === pageNum ? '#fff' : 'var(--foreground)' }}
              >
                {pageNum}
              </button>
            );
          })}
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-ghost px-2 py-1 text-xs disabled:opacity-40">›</button>
          <button onClick={() => setPage(totalPages)} disabled={page === totalPages} className="btn-ghost px-2 py-1 text-xs disabled:opacity-40">»</button>
        </div>
      </div>
    </div>
  );
}